import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import Location from './components/location/Location';
import CropManagement from './components/crop-management/CropManagement';
import OfficerIncharge from './components/officer-incharge/OfficerIncharge';
import Observation from './components/observation/Observation';
import FieldOperations from './components/field-operations/FieldOperations';
import SoilManagement from './components/soil-management/SoilManagement';
import Season from './components/season/Season';
import TrialCreation from './components/trial-creation/TrialCreation';
import ViewAssignment from './components/trial-creation/ViewAssignment';
import PlotOfficerDashboard from './components/plot-officer/PlotOfficerDashboard';
import DataEntry from './components/plot-officer/DataEntry';
import OfficerWorkspace from './components/plot-officer/OfficerWorkspace';
import ScientistDashboard from './components/scientist/ScientistDashboard';
import TrialUpdate from './components/scientist/TrialUpdate';
import TrialDataEntry from './components/admin/TrialDataEntry';
import ViewDataEntry from './components/data-entry/ViewDataEntry';
import Profile from './components/auth/Profile';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider, useLocation as useLocationContext } from './contexts/LocationContext';
import Login from './components/auth/Login';

// AppContent component to use hooks
const AppContent = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const { locationPermission, requestLocationPermission, locationError, isLoading, isCheckingPermission } = useLocationContext();
  const location = useLocation();

  // Redirect Plot Officers to their dashboard by default
  const defaultRoute = user?.role === 'plot-officer' || user?.role === 'officer'
    ? '/officer'
    : user?.role === 'scientist'
    ? '/scientist'
    : '/dashboard';

  // Auth guard: redirect unauthenticated users to login (except on /login)
  if (!loading && !isAuthenticated && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  // Officers should stay inside Officer Workspace; redirect any /trial-creation views back to /officer
  if (user?.role === 'officer' && location.pathname === '/trial-creation') {
    return <Navigate to={`/officer${location.search}`} replace />;
  }

  // Show Login page without Layout
  if (location.pathname === '/login') {
    return (
      <div className="min-h-screen">
        <Login />
      </div>
    );
  }

  // Standalone full-screen Trial Creation views (detail and locations)
  // Skip full-screen for officer logins and regular users - show within layout instead
  if (user?.role !== 'officer' && location.pathname === '/trial-creation' && 
      (location.search.includes('view=detail') || location.search.includes('view=locations'))) {
    console.log('Detail view detected - showing within layout:', location.pathname + location.search);
    // Don't render full-screen, let it render within the Layout component
  }

  return (
    <Layout>
      {locationPermission !== 'granted' && !isCheckingPermission && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-3">
              <svg className="w-6 h-6 text-secondary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c1.657 0 3-1.343 3-3S13.657 5 12 5 9 6.343 9 8s1.343 3 3 3z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.5 8A7.5 7.5 0 105 8c0 7.5 7 12 7 12s7-4.5 7-12z"/></svg>
              <h3 className="text-lg font-semibold text-gray-900">Enable Location Access</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">We use your location to tag field data accurately. Please allow location access to continue.</p>
            {locationError && (
              <p className="text-sm text-red-600 mb-2">{locationError}</p>
            )}
            <div className="flex justify-end space-x-3">
              <button onClick={async () => { await requestLocationPermission(); }} disabled={isLoading} className={`btn-primary ${isLoading ? 'opacity-80 cursor-not-allowed' : ''}`}>{isLoading ? 'Requesting...' : 'Allow Location'}</button>
            </div>
          </div>
        </div>
      )}
      <Routes>
        <Route path="/" element={<Navigate to={defaultRoute} />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/location" element={<Location />} />
        <Route path="/crop-management" element={<CropManagement />} />
        <Route path="/officer-incharge" element={<OfficerIncharge />} />
        <Route path="/observation" element={<Observation />} />
        <Route path="/field-operations" element={<FieldOperations />} />
        <Route path="/soil-management" element={<SoilManagement />} />
        <Route path="/season" element={<Season />} />
                  <Route path="/trial-creation" element={<TrialCreation />} />
        <Route path="/view-assignment" element={<ViewAssignment />} />
        <Route path="/trial-data-entry" element={<TrialDataEntry />} />
        <Route path="/view-data-entry" element={<ViewDataEntry />} />
        <Route path="/officer" element={<OfficerWorkspace />} />
        <Route path="/plot-officer" element={<PlotOfficerDashboard />} />
        <Route path="/data-entry" element={<DataEntry />} />
        <Route path="/scientist" element={<ScientistDashboard />} />
        <Route path="/trial-update" element={<TrialUpdate />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="*" element={<Navigate to={defaultRoute} />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <Router>
          <div className="App">
            <AppContent />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App; 